import React from 'react';
import { IndividualPricingQuote, quoteComparison } from '../../../services/individualPricingQuotations';
import IndividualPricingBasis from '../../pages/admin/IndividualPricingBasis';
import { usesSeparateFees } from '../../../services/individualPricingComparison';
import { pricingVersionLabel } from '../../../utils/pricingVersionLabel';
import './IndividualRepaymentSchedule.css';
export default function IndividualQuotationSummary({ quote }: { quote: IndividualPricingQuote }) {
  const separate = usesSeparateFees(quote.calculatorVersion);
  const money = (n: number | undefined | null) => n == null ? 'Not recorded' : `R ${Number(n).toFixed(2)}`;
  return <section aria-label="Selected pricing quotation"><h6>Selected pricing quotation</h6>
    <p className="text-break">Pricing version: {pricingVersionLabel(quote.pricing)}</p>
    <details><summary>Technical details</summary>Quotation ID: {quote.quoteId}<br />Pricing version ID: {quote.pricing.versionId}</details>
    <p>Cash received: {money(quote.funding.cashReceived)} · Financed initiation: {money(quote.funding.feeFinanced)}<br />
      Interest: {money(quote.totalInterest)} · Service fees before tax: {money(quote.totalServiceBase)} · Tax: {money(quote.totalTax)}<br />
      Total repayments: {money(quote.scheduledRepayments)}</p>
    <p>Initiation fee before tax: {money(quote.funding.initiationCharge?.baseAmount)} ·
      Initiation VAT: {money(quote.funding.initiationCharge?.taxAmount)}</p>
    <details className="mb-3"><summary>Applied pricing settings</summary>
      <IndividualPricingBasis result={quoteComparison(quote)} />
    </details>
    <IndividualChargePeriods ledger={quote.ledger} />
    <div className="table-responsive"><table className="table"><caption>Retained repayment schedule</caption>
      <IndividualRepaymentHeaders separateFees={separate} />
      <tbody>{quote.instalments.map(row => <tr key={row.dueDate}><td>{row.dueDate}</td>
        {[row.principal, ...(separate?[row.initiationFee]:[]), row.interest, row.serviceBase, row.serviceTax, row.payment, ...(separate?[row.closingBalance,row.closingInitiationBalance]:[])].map((n, i) => <td key={i}>{money(n)}</td>)}</tr>)}</tbody>
    </table></div></section>;
}

export function IndividualRepaymentHeaders({ separateFees, includeCombinedBalance = false }: {
  separateFees: boolean;
  includeCombinedBalance?: boolean;
}) {
  const columns = [
    ['Due date'],
    ...(separateFees
      ? [['Principal paid', 'Principal repayment'], ['Init. fee + VAT', 'Initiation fee incl. VAT']]
      : [['Cash + fee', 'Cash and financed fee repayment']]),
    ['Interest'], ['Service fee'], ['Service VAT'], ['Payment'],
    ...(separateFees
      ? [['Principal left', 'Outstanding principal'], ['Init. fee left', 'Outstanding initiation fee']]
      : includeCombinedBalance ? [['Balance']] : []),
  ];
  return <thead className="individual-repayment-schedule-header"><tr>{columns.map(([label, fullLabel]) =>
    <th key={label} scope="col" aria-label={fullLabel} title={fullLabel}>{label}</th>
  )}</tr></thead>;
}

/** Shared presentation of the server-retained calendar charge periods. */
export function IndividualChargePeriods({ ledger }: { ledger: IndividualPricingQuote['ledger'] }) {
  const money=(n: number) => `R ${Number(n).toFixed(2)}`;
  return (
    <details className="mb-3"><summary>Retained charge periods</summary>
      {ledger?.interest?.length ? <div className="table-responsive"><table className="table table-sm">
        <caption>Retained interest charge periods</caption>
        <thead><tr><th>From</th><th>Through</th><th>Assessed on</th><th>Collected on</th><th>Interest</th></tr></thead>
        <tbody>{ledger.interest.map((row, i) => <tr key={i}>
          <td>{row.fromInclusive}</td><td>{row.throughInclusive}</td><td>{row.assessedOn}</td><td>{row.collectedOn}</td><td>{money(row.amount)}</td>
        </tr>)}</tbody></table></div> : <p>Interest charge periods were not recorded in this quotation.</p>}
      {ledger?.service?.length ? <div className="table-responsive"><table className="table table-sm">
        <caption>Retained service fee periods</caption>
        <thead><tr><th>Month</th><th>From</th><th>Through</th><th>Assessed on</th><th>Collected on</th><th>Fee before tax</th><th>VAT</th><th>Total</th></tr></thead>
        <tbody>{ledger.service.map((row, i) => <tr key={i}>
          <td>{row.month}</td><td>{row.fromInclusive}</td><td>{row.throughInclusive}</td><td>{row.assessedOn}</td><td>{row.collectedOn}</td>
          <td>{money(row.charge.baseAmount)}</td><td>{money(row.charge.taxAmount)}</td><td>{money(row.charge.totalAmount)}</td>
        </tr>)}</tbody></table></div> : <p>Service fee periods were not recorded in this quotation.</p>}
    </details>
  );
}
